import React from "react";

const MINERVA_BANNER =
  "/images/philosophy/chester-s-minerva-shrine-banner.webp";

const VIDEO_ONE =
  "/videos/philosophy/minerva-shrine-01.mp4";

const VIDEO_TWO =
  "/videos/philosophy/minerva-shrine-02.mp4";

const SPOTIFY_URL =
  "https://open.spotify.com/episode/2RVVgo9WpveYTyh3bpmw6h?si=o6DjMkl5S3OpQKsZmnUtnA";

const buttonPrimary =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-3 font-medium text-white transition hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400/70";

const buttonSecondary =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-amber-600 px-5 py-3 font-medium text-amber-200 transition hover:bg-amber-600/10 focus:outline-none focus:ring-2 focus:ring-amber-400/70";

const whisperMovements = [
  {
    number: "I",
    title: "The Quarry",
    paragraphs: [
      "I have not moved.",
      "You have.",
      "For nearly two thousand years, I have remained within this face of soft red sandstone, drawn from the living rock through the rhythm of measurement, hammer and chisel.",
      "Soldiers brought me forth — legionaries of Deva Victrix, men whose lives depended upon discipline, judgement and strategy.",
      "I remember the sound of their work.",
      "Chisels striking stone. Blocks being squared for walls and gates. The immense labour of transforming a fortress of timber into one of stone.",
      "And yet, within all that practical, brutal effort, they made room for something sacred.",
      "They gave me form here, in what you now call Edgar’s Field, on the southern bank of the Dee, where the Roman road once carried soldiers, traders and administrators towards the fortress.",
      "I am Minerva.",
      "Virgin goddess. Keeper of wisdom and strategy. Patron of crafts, weaving, commerce and medicine.",
      "To those who carved me, I was imagined tall and armoured, carrying not the weapon of blind fury but the spear of precision — the weapon guided by thought.",
      "They knew my owl too, the silent watcher in darkness, seeing where others could not.",
      "Some, like the scholar Varro, understood me as something greater still: the intelligence behind order, the architecture of the universe itself.",
      "Perhaps that is why they brought me forth here.",
      "Not within a temple of marble, nor behind great columns, but directly from the living rock from which their fortress was being made.",
      "Around me the quarry rang with iron.",
      "Stone was cut, squared and carried away, down towards the river and onwards to Deva.",
      "Walls would rise from it. Gates. Towers. Barracks. Places in which men would sleep, work, watch and wait.",
      "Did those who carved me believe they were placing something of my spirit within that stone?",
      "That as each block was taken from this place and carried towards the fortress, some small measure of my protection travelled with it?",
      "Perhaps.",
      "A wall is more than stone when men believe a goddess watches over the hands that build it.",
      "Perhaps they imagined my wisdom within the measuring of the courses, my strength within the blocks themselves, my watchfulness carried across the river and set into the walls of Deva.",
      "Perhaps, while my image remained here, something of me travelled with every stone they took.",
      "I cannot tell you what those men believed. Only that they carved me here — not after the work was finished, not somewhere distant from the labour, but here, among the hammer blows, the stone dust and the scars of extraction.",
      "And so they left me in the quarry face, watching over the place from which their fortress grew.",
      "They did more than leave a mark.",
      "They gave the rock a guardian."
    ]
  },
  {
    number: "II",
    title: "The Long Watching",
    paragraphs: [
      "And I remained.",
      "My eyes have weathered now. My wisdom has no roof above it, no guardian at the gate. I am neither monumental nor carefully curated. Rain reaches me. Pollution settles upon me. Wind and water patiently soften the grain.",
      "Lichen moves slowly across my features.",
      "Children play nearby. Houses press close. Conversations about signs, protection and preservation rise and fade with the years, drifting through civic attention like mist from the river.",
      "The stone is paler than it once was. The lines of my body have become uncertain.",
      "Yet enough remains.",
      "Enough, perhaps, to make you stop.",
      "I am a fragment, certainly, but fragments can disturb the comfortable belief that only great monuments endure.",
      "I have survived without ceremony, without grandeur, almost without notice.",
      "And still I am here.",
      "I have watched the centuries gather around me.",
      "When Rome loosened its hold upon Britannia and Deva changed, the fortress quietened, buildings failed and other lives began among the remains.",
      "The river continued beneath the bridge of time, indifferent to the names given to those who crossed it.",
      "But I had already been brought from the stone.",
      "So I stayed.",
      "Through those quieter centuries I watched the ground change, and if stone can dream, perhaps I dreamed with it — weaving the old place into everything that followed."
    ]
  },
  {
    number: "III",
    title: "The City Remembers",
    paragraphs: [
      "Sometimes I wonder whether you might imagine my hand in what came next.",
      "Look at the city that rose among the Roman remains.",
      "It did not grow entirely without memory. Roads persisted. Crossings endured. Walls followed ancient lines. Craftsmen shaped wood, metal and stone. Traders gathered. Generations built upon foundations whose makers they could no longer name.",
      "Commerce and craft were always among my domains.",
      "When Saxon hands worked at the Dee, shaping timber and stone to harness the force of the water, perhaps some faint echo of that older intelligence remained — not because I commanded them, but because people have always learned by watching, measuring, remembering and making.",
      "And those things belong to me too.",
      "I remember people coming past.",
      "Pilgrims making their way towards the churches that grew within and around the old fortress. Travellers approaching the river. Children lingering before the figure in the stone.",
      "Perhaps some no longer knew who I was.",
      "A goddess? A saint? The Virgin Mary? Or simply the woman in the rock who had always been there?",
      "Names change more easily than places.",
      "If flowers were placed before me, I received them. If a hand rested for a moment against the sandstone, I felt its warmth. If someone looked upon me and wondered, that was enough.",
      "The centuries continued.",
      "I heard celebration after victories whose names are now scarcely remembered. I heard the thunder of hooves and felt cannon fire shudder through the porous sandstone around me.",
      "Later came other sounds.",
      "The rise and falling wail of a siren. Hurried footsteps crossing the bridge. Another generation wondering whether those they loved would return home.",
      "Through each age, people looked towards the same river and crossed much the same ground, carrying fears that would have been understood by those who came before them.",
      "And above it all, in imagination at least, my owl continued its silent watch."
    ]
  },
  {
    number: "IV",
    title: "The Seam",
    paragraphs: [
      "I have become a seam between things that are too often separated: labour and belief, empire and ordinary life, departure and return, endurance and erosion.",
      "Where power once spoke loudly, the rock now whispers.",
      "It is easy, from the distance of centuries, to judge an empire by what it conquered.",
      "Harder, perhaps, is recognising how deeply the material traces of those centuries remain beneath your feet.",
      "Roads still follow the lie of the land. Crossings remain where the river allows them. Settlements continue where ground, water and stone make habitation possible.",
      "The people change.",
      "The land remembers differently.",
      "And I remain somewhere between the two.",
      "Does my presence still affect you?",
      "Perhaps not in the way those soldiers imagined.",
      "But when you walk Chester’s walls, cross the Dee, work in its shops, hospitals, workshops or universities, you still move through a landscape shaped by innumerable acts of thought and labour.",
      "Sandstone has little interest in human fashion. It simply endures for as long as it can.",
      "What survives here is not the rhetoric of empire.",
      "It is workmanship. Memory. Chance.",
      "And the extraordinary fact that a carving cut by soldiers nearly two thousand years ago still occupies the same quarry face while emperors, kingdoms, monarchies, parliaments and councils have risen and disappeared around it.",
      "Small things do not always vanish because they are unimportant.",
      "Sometimes they vanish because no one looked for long enough."
    ]
  },
  {
    number: "V",
    title: "The Crossing",
    paragraphs: [
      "And so I watch you now.",
      "You cross the old Dee Bridge into Handbridge. You enter Edgar’s Field and walk towards the quarry face.",
      "At first there may appear to be little there.",
      "Then your eyes adjust.",
      "A line emerges. A shoulder. The suggestion of a figure.",
      "Around me you begin to notice the scars of extraction — the practical marks left by men cutting stone for a fortress, surrounding the quieter mark they made for something they considered sacred.",
      "Then perhaps you become aware of everything gathered around this one place.",
      "Quarry. River. Road. Fortress. Bridge. City.",
      "Lives layered over lives, all within a few hundred yards.",
      "For a moment, the distance between them narrows.",
      "You are no longer simply looking at an old carving.",
      "You are standing at a crossing point in time.",
      "I do not ask you to worship me.",
      "I do not ask you even to understand me.",
      "Only to notice.",
      "Because I am not merely a relic of something that has disappeared.",
      "I am one small continuity within a landscape that has carried human labour, belief, fear, hope and memory for longer than any one life can easily comprehend."
    ]
  },
  {
    number: "VI",
    title: "The Guardian",
    paragraphs: [
      "Perhaps that is why my survival matters.",
      "Not because I am grand.",
      "Because I am vulnerable.",
      "Once, perhaps, men stood here believing that I might watch over their labour — that something of my presence might travel with the stone they carried across the river and set into the walls of Deva.",
      "Now the relationship has quietly reversed.",
      "The guardian has become something that needs guarding.",
      "Rain continues to fall. The sandstone softens. My features grow fainter.",
      "And yet I am still here.",
      "Perhaps preservation begins with something simpler than monuments or barriers.",
      "Someone notices. Someone stops. Someone remembers.",
      "Once, stone was carried from this quarry across the Dee.",
      "Now you carry something away instead.",
      "Not stone.",
      "Memory.",
      "I have watched the river move beneath changing skies for almost two thousand years, while the world beyond this rock has altered beyond recognition.",
      "And still, quietly, I remain."
    ]
  }
];

function Film({ src, title, caption }) {
  return (
    <figure>
      <div className="overflow-hidden rounded-3xl border border-amber-700/30 bg-black shadow-2xl">
        <video
          className="aspect-video w-full bg-black object-contain"
          controls
          playsInline
          preload="metadata"
          poster={MINERVA_BANNER}
          aria-label={title}
        >
          <source src={src} type="video/mp4" />
          Your browser does not support embedded video. You can still listen to
          the full episode on Spotify.
        </video>
      </div>
      <figcaption className="mt-4 text-sm leading-6 text-neutral-500">
        {caption}
      </figcaption>
    </figure>
  );
}

export default function MinervaShrine() {
  return (
    <main className="min-h-screen overflow-hidden bg-[#080a0d] text-white">
      {/* HERO */}
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0d1722] to-[#080a0d] px-4 pb-16 pt-8 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <figure className="overflow-hidden rounded-3xl border border-amber-200/20 bg-black shadow-2xl">
            <div className="relative">
              <img
                src={MINERVA_BANNER}
                alt="Chester’s Minerva Shrine — The Quiet Shift"
                className="aspect-[21/9] w-full object-cover"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-14">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-300 sm:text-sm">
                  The Quiet Shift · Watch &amp; Explore
                </p>
              </div>
            </div>

            <figcaption className="border-t border-white/10 bg-[#0b0d10] px-5 py-5 sm:px-7">
              <p className="font-serif text-lg font-semibold text-amber-100 sm:text-xl">
                Minerva’s Shrine, Edgar’s Field, Chester — c. 100 CE
              </p>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-neutral-400 sm:text-base">
                Carved into the living sandstone beside the Roman quarry that
                supplied stone for Deva, the shrine was created in the early
                second century, during the occupation of the fortress by Legio
                XX Valeria Victrix.
              </p>
            </figcaption>
          </figure>

          <div className="mx-auto max-w-4xl pb-2 pt-14 text-center">
            <p className="font-serif text-4xl italic leading-tight text-amber-100 sm:text-5xl">
              “I have not moved.”
            </p>
            <p className="mt-2 font-serif text-4xl italic leading-tight text-neutral-300 sm:text-5xl">
              “You have.”
            </p>
            <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-neutral-300">
              A short visual and reflective journey into Chester’s Minerva
              Shrine: an invitation to notice something vulnerable, stand with
              it for a moment, and help carry its story forward.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a href="#film-one" className={buttonSecondary}>
                Begin the journey ↓
              </a>
              <a
                href={SPOTIFY_URL}
                target="_blank"
                rel="noreferrer"
                className={buttonPrimary}
              >
                Listen on Spotify
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FILM ONE */}
      <section
        id="film-one"
        className="scroll-mt-24 px-4 py-16 sm:px-6 lg:py-20"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400">
              Film I
            </p>
            <h2 className="mt-3 font-serif text-4xl font-semibold text-amber-100 sm:text-5xl">
              The face in the quarry
            </h2>
            <p className="mt-5 text-lg leading-8 text-neutral-400">
              Begin with the physical place: sandstone, extraction marks, a
              Roman road, the river and a sacred image cut directly into the
              working landscape of Deva.
            </p>
          </div>

          <Film
            src={VIDEO_ONE}
            title="Chester’s Minerva Shrine — film one"
            caption="A short visual introduction to the shrine and the landscape that surrounds it."
          />
        </div>
      </section>

      {/* WHISPER OF ROCK */}
      <section className="border-y border-white/10 bg-white/[0.025] px-4 py-16 sm:px-6 lg:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-400">
              A reflective interpretation
            </p>
            <h2 className="mt-4 font-serif text-4xl font-semibold text-amber-100 sm:text-5xl lg:text-6xl">
              The Whisper of Rock
            </h2>
            <p className="mt-3 font-serif text-2xl italic text-neutral-300 sm:text-3xl">
              The Architect of Time
            </p>

            <p className="mx-auto mt-7 max-w-3xl text-base leading-7 text-neutral-400 sm:text-lg sm:leading-8">
              The archaeological setting is documented. Minerva’s voice — and
              the suggestion that her protection may have travelled with the
              quarried stone into Deva — is an imaginative interpretation
              inspired by that setting.
            </p>

            <div className="mx-auto mt-12 max-w-3xl border-y border-amber-500/20 py-10">
              <p className="font-serif text-4xl italic leading-tight text-amber-100 sm:text-5xl">
                “I have not moved.”
              </p>
              <p className="mt-3 font-serif text-4xl italic leading-tight text-neutral-300 sm:text-5xl">
                “You have.”
              </p>
            </div>
          </div>

          <details className="group mt-12 overflow-hidden rounded-3xl border border-amber-500/25 bg-black/35 shadow-2xl">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 px-6 py-6 text-left transition hover:bg-white/[0.035] sm:px-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
                  Minerva speaks
                </p>
                <p className="mt-2 font-serif text-2xl text-amber-100 sm:text-3xl">
                  Read the full reflection
                </p>
              </div>
              <span
                aria-hidden="true"
                className="text-2xl text-amber-300 transition-transform group-open:rotate-180"
              >
                ↓
              </span>
            </summary>

            <div className="border-t border-white/10 px-6 py-10 sm:px-8 lg:px-12 lg:py-14">
              <div className="space-y-16">
                {whisperMovements.map((movement) => (
                  <article
                    key={movement.number}
                    className="mx-auto max-w-3xl"
                  >
                    <div className="mb-7 flex items-baseline gap-4">
                      <span className="font-serif text-2xl text-amber-500/80">
                        {movement.number}
                      </span>
                      <h3 className="font-serif text-3xl text-amber-100 sm:text-4xl">
                        {movement.title}
                      </h3>
                    </div>

                    <div className="space-y-5 text-lg leading-8 text-neutral-300">
                      {movement.paragraphs.map((paragraph, index) => (
                        <p
                          key={`${movement.number}-${index}`}
                          className={
                            paragraph === "I have not moved." ||
                            paragraph === "You have." ||
                            paragraph === "They gave the rock a guardian." ||
                            paragraph ===
                              "The guardian has become something that needs guarding."
                              ? "font-serif text-2xl italic leading-relaxed text-amber-100 sm:text-3xl"
                              : ""
                          }
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* FILM TWO */}
      <section className="px-4 py-16 sm:px-6 lg:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400">
              Film II
            </p>
            <h2 className="mt-3 font-serif text-4xl font-semibold text-amber-100 sm:text-5xl">
              The crossing point in time
            </h2>
            <p className="mt-5 text-lg leading-8 text-neutral-400">
              Look again, this time with the centuries in mind: the extraction
              scars around the relief, the Dee below it, the old crossing and
              the modern city beyond.
            </p>
          </div>

          <Film
            src={VIDEO_TWO}
            title="Chester’s Minerva Shrine — film two"
            caption="A second short reflection on survival, erosion and the act of noticing."
          />
        </div>
      </section>

      {/* CROSSING POINT */}
      <section className="border-y border-white/10 bg-gradient-to-b from-neutral-950 to-[#0d1722] px-4 py-20 sm:px-6 lg:py-28">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400">
            Stand at the crossing
          </p>
          <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight text-amber-100 sm:text-5xl lg:text-6xl">
            Quarry. River. Road. Fortress. Bridge. City.
          </h2>
          <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-neutral-300">
            All layered within a few hundred yards. To stand before the shrine
            is not simply to observe a carving. It is to occupy one small point
            where labour, belief, erosion, memory and the living city meet.
          </p>

          <blockquote className="mx-auto mt-12 max-w-3xl font-serif text-3xl italic leading-relaxed text-amber-100 sm:text-4xl">
            “You are standing at a crossing point in time.”
          </blockquote>
        </div>
      </section>

      {/* RITUAL ENDING */}
      <section className="px-4 py-20 sm:px-6 lg:py-28">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-3xl border border-amber-500/25 bg-black/50 p-8 text-center shadow-2xl sm:p-12 lg:p-16">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-amber-400">
              A final act of noticing
            </p>

            <div className="mt-8 space-y-3 font-serif text-3xl leading-tight text-amber-50 sm:text-4xl lg:text-5xl">
              <p>Stand here for sixty seconds.</p>
              <p>Say nothing.</p>
              <p>Let the traffic pass.</p>
              <p>Let the river move.</p>
              <p>Let the city continue around you.</p>
              <p className="pt-4 text-amber-200">Let the stone remain.</p>
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <a
                href={SPOTIFY_URL}
                target="_blank"
                rel="noreferrer"
                className={buttonPrimary}
              >
                Listen to the full episode on Spotify
                <span aria-hidden="true">↗</span>
              </a>
              <a href="/philosophy" className={buttonSecondary}>
                ← Return to The Quiet Shift
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
